import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';
import './SlotPicker.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// FONCTION HARD-CODÉE - Retourne les créneaux EXACTS selon le jour
// JavaScript: getDay() => 0=Dimanche, 1=Lundi, 2=Mardi, 3=Mercredi, 4=Jeudi, 5=Vendredi, 6=Samedi
const getHardcodedSlots = (dayOfWeek) => {
  switch (dayOfWeek) {
    case 0: // Dimanche - FERMÉ
    case 1: // Lundi - FERMÉ
      return [];
    
    case 2: // Mardi
    case 3: // Mercredi
    case 4: // Jeudi
    case 5: // Vendredi
      // 09h00-12h00 + 14h00-19h00 (dernier créneau 18:30)
      return [
        "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
        "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
        "17:00", "17:30", "18:00", "18:30"
      ];
    
    case 6: // Samedi - 09h00-16h00 (dernier créneau 15:30)
      return [
        "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
        "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
        "15:00", "15:30"
      ];
    
    default:
      return [];
  }
};

export const SlotPicker = ({ onSlotSelect, selectedSlot }) => {
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [selectedDay, setSelectedDay] = useState(null);
  const [availableDays, setAvailableDays] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  // Générer 14 jours à partir d'aujourd'hui
  useEffect(() => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      date.setHours(0, 0, 0, 0);
      days.push(date);
    }
    setAvailableDays(days);
    setSelectedDay(days[0]);
  }, []);

  // Charger les créneaux depuis l'API backend
  useEffect(() => {
    if (!selectedDay) return;

    const dayOfWeek = selectedDay.getDay();
    
    // Si fermé (dimanche=0 ou lundi=1 en JavaScript)
    if (dayOfWeek === 0 || dayOfWeek === 1) {
      setSlots([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Formater la date en local (SANS conversion UTC)
    const year = selectedDay.getFullYear();
    const month = String(selectedDay.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDay.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Appel API avec cache-busting pour éviter les doublons
    fetch(`${BACKEND_URL}/api/calendar/available-slots?date=${dateStr}&_t=${Date.now()}`)
      .then(res => res.json())
      .then(data => {
        if (data.slots) {
          const slotsObjects = data.slots.map(slot => {
            // Extraire l'heure directement de la chaîne ISO (format: 2026-02-17T09:00:00+01:00)
            // On prend les caractères 11-16 qui correspondent à HH:MM
            const timeStr = slot.start.substring(11, 16);
            
            return {
              time: timeStr,
              start: slot.start,
              end: slot.end,
              available: slot.available
            };
          });
          setSlots(slotsObjects);
        } else {
          // Fallback aux créneaux hardcodés si erreur
          const hardcodedSlots = getHardcodedSlots(dayOfWeek);
          const slotsObjects = hardcodedSlots.map(time => {
            const [hour, minute] = time.split(':').map(Number);
            const start = new Date(selectedDay);
            start.setHours(hour, minute, 0, 0);
            const end = new Date(start);
            end.setMinutes(end.getMinutes() + 30);
            
            return {
              time: time,
              start: start.toISOString(),
              end: end.toISOString(),
              available: true
            };
          });
          setSlots(slotsObjects);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching slots:', err);
        // Fallback aux créneaux hardcodés
        const hardcodedSlots = getHardcodedSlots(dayOfWeek);
        const slotsObjects = hardcodedSlots.map(time => {
          const [hour, minute] = time.split(':').map(Number);
          const start = new Date(selectedDay);
          start.setHours(hour, minute, 0, 0);
          const end = new Date(start);
          end.setMinutes(end.getMinutes() + 30);
          
          return {
            time: time,
            start: start.toISOString(),
            end: end.toISOString(),
            available: true
          };
        });
        setSlots(slotsObjects);
        setLoading(false);
      });
  }, [selectedDay]);

  // Navigation : 3 jours à la fois
  const visibleDays = availableDays.slice(currentDayIndex, currentDayIndex + 3);

  const goToPrevious = () => {
    if (currentDayIndex > 0) {
      setCurrentDayIndex(currentDayIndex - 3);
    }
  };

  const goToNext = () => {
    if (currentDayIndex + 3 < availableDays.length) {
      setCurrentDayIndex(currentDayIndex + 3);
    }
  };

  const formatDayShort = (date) => {
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString('fr-FR', options);
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const handleSlotClick = (slot) => {
    if (!slot.available) return;
    
    const dateStr = selectedDay.toISOString().split('T')[0];
    onSlotSelect({
      date: dateStr,
      start: slot.start,
      end: slot.end,
      displayTime: slot.time,
      displayDate: formatDayShort(selectedDay)
    });
  };

  const isSlotSelected = (slot) => {
    if (!selectedSlot) return false;
    return selectedSlot.displayTime === slot.time;
  };

  return (
    <div className="slot-picker-compact">
      {/* Navigation 3 jours */}
      <div className="days-nav-compact">
        <button
          type="button"
          className="nav-btn-compact"
          onClick={goToPrevious}
          disabled={currentDayIndex === 0}
        >
          <ChevronLeft size={20} />
        </button>

        <div className="days-compact">
          {visibleDays.map((day, idx) => {
            const selected = isSameDay(day, selectedDay);
            const today = isToday(day);

            return (
              <button
                key={idx}
                type="button"
                className={`day-btn-compact ${
                  selected ? 'selected' : ''
                } ${today ? 'today' : ''}`}
                onClick={() => handleDayClick(day)}
              >
                <div className="day-label-compact">
                  {formatDayShort(day)}
                </div>
                {today && <span className="today-badge-compact">Auj.</span>}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="nav-btn-compact"
          onClick={goToNext}
          disabled={currentDayIndex + 3 >= availableDays.length}
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Créneaux */}
      <div className="slots-area-compact">
        {loading && (
          <div className="slots-loading">
            Chargement des disponibilités
          </div>
        )}

        {!loading && slots.length === 0 && (
          <div className="no-slots-compact">
            <Calendar size={32} />
            <p>Fermé</p>
          </div>
        )}

        {!loading && slots.length > 0 && (
          <div className="slots-grid-compact">
            {slots.map((slot, idx) => (
              <button
                key={idx}
                type="button"
                className={`slot-btn-compact ${
                  isSlotSelected(slot) ? 'selected' : ''
                } ${!slot.available ? 'unavailable' : ''}`}
                onClick={() => handleSlotClick(slot)}
                disabled={!slot.available}
              >
                <Clock size={12} />
                <span>{slot.time}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation */}
      {selectedSlot && (
        <div className="confirm-compact">
          <div className="confirm-icon-compact">✓</div>
          <div className="confirm-text-compact">
            <strong>{selectedSlot.displayDate}</strong> à{' '}
            <strong>{selectedSlot.displayTime}</strong>
          </div>
        </div>
      )}
    </div>
  );
};